import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Building2, Search } from "lucide-react";
import { toast } from "sonner";
import type { OrganizationType } from "@bookdental/schemas";
import { Popover, PopoverContent, PopoverTrigger } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { useSwitchOrganization } from "@/lib/use-switch-organization";
import { recarregarNaOrganizacao } from "@/lib/recarregar-na-organizacao";
import { tipoDaOrganizacao } from "./AvatarDaOrganizacao";
import {
  GatilhoDaOrganizacao,
  LinhaDaOrganizacao,
  ROLE_LABEL,
  type OrganizacaoDoSwitcher,
  type VarianteDoSwitcher,
} from "./OrganizationSwitcherPartes";

const GROUP_ORDER = ["Pessoal", "Clínicas", "Laboratórios", "Clínica e laboratório", "Radiologia"] as const;
// A partir daqui, procurar pelo nome é mais rápido que rolar.
const COM_BUSCA = 6;

/** Rótulo da seção do dropdown pra uma organização — separa Pessoal/Clínicas/Laboratórios em vez
 *  de listar tudo misturado, evitando confusão sobre o escopo de atuação no momento. */
function groupLabel(org: { isPersonal: boolean; types: OrganizationType[] }): (typeof GROUP_ORDER)[number] {
  if (org.isPersonal) return "Pessoal";
  const isLab = org.types.includes("DENTAL_LAB");
  const isOffice = org.types.includes("DENTAL_OFFICE");
  if (isLab && isOffice) return "Clínica e laboratório";
  if (isLab) return "Laboratórios";
  if (isOffice) return "Clínicas";
  return "Radiologia";
}

type Org = { id: string; name: string; isPersonal: boolean; types: OrganizationType[]; logoUrl?: string | null };

/** No gatilho e na ativa (sem título de grupo em cima): tipo e papel. Dentro de um grupo, o título
 *  já diz o tipo: só o papel, e a pessoal fica numa linha só. */
const paraOSwitcher = (org: Org, role: string, noGrupo = false): OrganizacaoDoSwitcher => {
  const papel = ROLE_LABEL[role] ?? role;
  const detalhe = org.isPersonal ? (noGrupo ? null : "Pessoal") : noGrupo ? papel : `${tipoDaOrganizacao(org.types)} · ${papel}`;
  return { id: org.id, nome: org.name, logo: org.logoUrl, pessoal: org.isPersonal, detalhe };
};

export function OrganizationSwitcher({ variante = "barra" }: { variante?: VarianteDoSwitcher }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busca, setBusca] = useState("");

  const meQuery = trpc.me.current.useQuery();
  // Sempre ligada: o gatilho mostra o logo da organização ativa, que vem daqui.
  const listQuery = trpc.organization.listMine.useQuery(undefined, { staleTime: 5 * 60_000 });
  // Sempre ligada (não só com o dropdown aberto): o ponto no gatilho é o que avisa que existe
  // não lida em OUTRA organização — ninguém abre o dropdown sem um motivo pra abrir.
  const unreadQuery = trpc.notification.unreadCountByOrg.useQuery();
  const naoLidas = unreadQuery.data ?? {};
  const { switchTo, isSwitching } = useSwitchOrganization();

  const handleSwitch = (organizationId: string) => {
    const alvo = (listQuery.data ?? []).find((m) => m.organization.id === organizationId)?.organization;
    switchTo(organizationId)
      .then(() => {
        setOpen(false);
        // Recarga em vez de só invalidar: garante que nada da organização anterior sobreviva em
        // estado de componente. Cai em /inicio quando a rota atual não existe pro tipo da nova.
        recarregarNaOrganizacao(alvo);
      })
      .catch((err) => toast.error(err.message || "Erro ao trocar de organização."));
  };

  const active = meQuery.data?.activeOrganization;
  const lista = listQuery.data ?? [];
  const termo = busca.trim().toLowerCase();
  const others = lista.filter((m) => m.organization.id !== active?.id && (!termo || m.organization.name.toLowerCase().includes(termo)));

  const groups = useMemo(() => {
    const byLabel = new Map<string, typeof others>();
    for (const m of others) {
      const label = groupLabel(m.organization);
      byLabel.set(label, [...(byLabel.get(label) ?? []), m]);
    }
    return GROUP_ORDER.map((label) => ({ label, items: byLabel.get(label) ?? [] })).filter((g) => g.items.length > 0);
  }, [others]);

  if (!active) return null;

  // O sino já cobre a organização ativa — o ponto no gatilho só acende pra não lida nas OUTRAS.
  const temNasOutras = Object.entries(naoLidas).some(([orgId, n]) => orgId !== active.id && n > 0);
  const logoDaAtiva = lista.find((m) => m.organization.id === active.id)?.organization.logoUrl;
  const ativa = paraOSwitcher({ id: active.id, name: active.name, isPersonal: active.isPersonal, types: active.types, logoUrl: logoDaAtiva }, active.role);

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) setBusca("");
      }}
    >
      <PopoverTrigger asChild>
        <GatilhoDaOrganizacao variante={variante} organizacao={ativa} avisoNasOutras={temNasOutras} />
      </PopoverTrigger>
      <PopoverContent align="center" sideOffset={8} className="w-80 overflow-hidden rounded-xl border border-zinc-200 bg-white p-0 shadow-2xl dark:border-white/10 dark:bg-[#17171c]">
        {lista.length > COM_BUSCA && (
          <div className="border-b border-zinc-100 p-2 dark:border-border">
            <label className="flex items-center gap-2 rounded-md border border-zinc-200 px-2.5 dark:border-border">
              <Search className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
              <input
                autoFocus
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar organização"
                className="h-8 w-full bg-transparent text-sm outline-none placeholder:text-zinc-400"
              />
            </label>
          </div>
        )}
        <div className="max-h-96 overflow-y-auto p-1.5">
          <LinhaDaOrganizacao organizacao={ativa} naoLidas={naoLidas[active.id] ?? 0} selected />
          {groups.map((group) => (
            // Uma linha fina entre os grupos separa sem caixa: caixa dentro do menu seria borda
            // dentro de borda e menos organizações à vista.
            <div key={group.label} className="mt-1.5 border-t border-zinc-100 pt-1 dark:border-border">
              <p className="px-2.5 pb-1 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">{group.label}</p>
              {group.items.map((m) => (
                <button key={m.organization.id} type="button" onClick={() => handleSwitch(m.organization.id)} disabled={isSwitching} className="w-full text-left disabled:opacity-50">
                  <LinhaDaOrganizacao organizacao={paraOSwitcher(m.organization, m.role, true)} naoLidas={naoLidas[m.organization.id] ?? 0} />
                </button>
              ))}
            </div>
          ))}
          {termo && groups.length === 0 && <p className="px-2.5 py-3 text-center text-xs text-zinc-500 dark:text-muted-foreground">Nenhuma organização com esse nome.</p>}
        </div>
        <div className="border-t border-border p-1.5">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/organizacoes");
            }}
            className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-sm font-medium text-brand-700 transition-colors hover:bg-brand-50 dark:text-primary dark:hover:bg-primary/10"
          >
            <Building2 className="h-4 w-4" />
            Gerenciar organizações
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
